import type { Contract } from '@kalos/kalos-rpc';
import { format } from 'date-fns';

import { CONTRACT_SETTLED_PAYMENT_STATUSES } from '../constants';

/** Whether today falls inside the contract's start/end dates (date-only compare on the local day). */
export const coversToday = (
  contract: Contract,
  today = format(new Date(), 'yyyy-MM-dd'),
): boolean =>
  !!contract.dateStarted &&
  !!contract.dateEnded &&
  contract.dateStarted.slice(0, 10) <= today &&
  contract.dateEnded.slice(0, 10) >= today;

export type ContractBadgeStatus = 'Inactive' | 'Not active yet' | 'Expired' | 'Active';

/** Contract badge wording: Inactive, Not active yet before its start, Expired after its end, else Active. */
export const contractBadgeStatus = (
  contract: Contract,
  today = format(new Date(), 'yyyy-MM-dd'),
): ContractBadgeStatus => {
  if (contract.isActive === 0) return 'Inactive';
  if ((contract.dateStarted ?? '').slice(0, 10) > today) return 'Not active yet';
  const end = (contract.dateEnded ?? '').slice(0, 10);
  // A 0001-01-01 end is the empty-date sentinel, not an expiry.
  if (end >= '1900-01-01' && end < today) return 'Expired';
  return 'Active';
};

/** Badge colors for each `contractBadgeStatus`. */
export const CONTRACT_BADGE_CLASS: Record<ContractBadgeStatus, string> = {
  Inactive: 'bg-gray-100 text-gray-600',
  'Not active yet': 'bg-sky-100 text-sky-800',
  Expired: 'bg-red-100 text-red-800',
  Active: 'bg-green-100 text-green-800',
};

/**
 * A contract currently in effect: is_active, past review (not "pending"), settled
 * (Paid/Collected/Paid on contract), and today within the contract's start/end dates.
 */
export const isContractCurrent = (
  contract: Contract,
  today = format(new Date(), 'yyyy-MM-dd'),
): boolean =>
  contract.isActive === 1 &&
  contract.reviewStatus !== 'pending' &&
  CONTRACT_SETTLED_PAYMENT_STATUSES.includes(contract.paymentStatus) &&
  coversToday(contract, today);

/**
 * An open term that lists the property. Only such a contract flags a missing membership; a closed
 * or future term without one is expected.
 */
export const contractCoversProperty = (contract: Contract, propertyId: number): boolean =>
  propertyId > 0 &&
  contract.isActive === 1 &&
  coversToday(contract) &&
  (contract.properties ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .includes(String(propertyId));
